import type { Centre } from '@appTypes/auth';
import { ALL_CENTRES } from '@appTypes/auth';
import env from '@config/env';
import { ROUTES } from '@constants/routes';
import { invalidateDropdown } from '@hooks/useDropdown';
import { setAccessToken } from '@lib/axios';
import { queryClient } from '@lib/queryClient';
import { refreshSessions } from '@lib/refreshSessions';
import { connectSocket, disconnectSocket, getSocket, refreshSocketAuth } from '@lib/socket';
import { ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AppRouter } from '@routes/AppRouter';
import { authService } from '@services/auth.service';
import { notificationService } from '@services/notification.service';
import { useAppShellStore } from '@store/appShellStore';
import { useAuthStore } from '@store/authStore';
import { theme } from '@styles/theme';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useRef } from 'react';

const ThemedApp = () => {
  const { isAuthenticated, bootstrapped, user } = useAuthStore();
  const { shellReady, mainCentreId, centreId, sessionId } = useAppShellStore();

  // Guard against React 18 StrictMode double-invoking this effect in development.
  // POST /auth/refresh rotates (consumes) the token server-side, so a second call
  // with the already-consumed cookie returns 401 and logs the user out.
  const bootstrapFired = useRef(false);

  // Step 1 — auth bootstrap: restore access token from httpOnly refresh cookie.
  // Skipped on auth pages (/login, /forgot-password) — no valid session exists
  // there, so the call would always 401. We still call setBootstrapped() so
  // GuestRoute / ProtectedRoute unblock immediately.
  useEffect(() => {
    if (bootstrapFired.current) return;
    bootstrapFired.current = true;

    const AUTH_PATHS = [ROUTES.LOGIN, ROUTES.FORGOT_PASSWORD];
    if (AUTH_PATHS.includes(window.location.pathname as typeof ROUTES.LOGIN)) {
      useAuthStore.getState().setBootstrapped();
      return;
    }

    authService
      .refresh()
      .then(({ data }) => {
        setAccessToken(data.accessToken);
        useAuthStore.getState().setAuth(data.user);
      })
      .catch(() => {})
      .finally(() => {
        useAuthStore.getState().setBootstrapped();
      });
  }, []);

  // Step 2 — shell bootstrap: load main centres + centres + sessions once auth
  // is resolved, then settle on a (main centre, centre) pair. The centre list
  // kept in the store is always narrowed to the resolved main centre, so the
  // top-bar switcher can only ever offer siblings of the current centre.
  useEffect(() => {
    if (!bootstrapped) return;

    if (!isAuthenticated) {
      useAppShellStore.getState().resetShell();
      return;
    }

    const savedMainCentreId = localStorage.getItem('mainCentreId') ?? '';
    const savedCentreId = localStorage.getItem('centreId') ?? '';
    const savedSessionId = localStorage.getItem('sessionId') ?? '';

    Promise.all([
      authService.getMainCentres(),
      authService.getCentres(),
      authService.getSessions(),
      authService.getCurrentSession(),
    ])
      .then(([mainCentresRes, centresRes, sessionsRes, curSessionRes]) => {
        const mainCentres = mainCentresRes.data.items;
        const allCentres = centresRes.data.items;
        const sessions = sessionsRes.data.items;
        const curSessionId = curSessionRes.data.session._id;

        // Staff are locked to their assigned centre — and therefore to that
        // centre's main centre. Admins pick their last-used/active one.
        const isStaff = user?.role !== 'admin';
        const staffCentre = allCentres.find((c) => c._id === user?.centreId);

        const resolvedMainCentre = isStaff
          ? (mainCentres.find((m) => m._id === staffCentre?.mainCentreId) ??
             mainCentres.find((m) => m.active) ??
             mainCentres[0])
          : (mainCentres.find((m) => m._id === savedMainCentreId) ??
             mainCentres.find((m) => m.active) ??
             mainCentres[0]);

        const mainCentreId = resolvedMainCentre?._id ?? savedMainCentreId;
        const centres = allCentres.filter((c) => c.mainCentreId === mainCentreId);

        // A saved "all centres" rollup survives a reload, but only for admins.
        const savedIsRollup = !isStaff && savedCentreId === ALL_CENTRES;
        const resolvedCentre = isStaff
          ? (staffCentre ?? centres.find((c) => c.active) ?? centres[0])
          : (centres.find((c) => c._id === savedCentreId) ??
             centres.find((c) => c.active) ??
             centres[0]);

        const resolvedSessionId =
          sessions.find((s) => s._id === savedSessionId)?._id ?? curSessionId;

        useAppShellStore.getState().setShellData({
          mainCentres,
          centres,
          sessions,
          mainCentreId,
          centreId: savedIsRollup ? ALL_CENTRES : (resolvedCentre?._id ?? ''),
          sessionId: resolvedSessionId,
        });
      })
      .catch(() => {})
      .finally(() => {
        useAppShellStore.getState().setShellReady();
      });
  }, [bootstrapped, isAuthenticated]);

  // Step 3 — proactive token refresh every 14 min (access token expires at ~15 min).
  // Also reconnects the socket with the new token so it stays on the current identity.
  useEffect(() => {
    if (!shellReady) return;
    const t = setInterval(() => {
      authService
        .refresh()
        .then(({ data }) => {
          setAccessToken(data.accessToken);
          refreshSocketAuth();
        })
        .catch(() => {});
    }, 14 * 60 * 1000);
    return () => clearInterval(t);
  }, [shellReady]);

  // Step 4 — Socket.IO: connect when scope is ready, disconnect on logout/unmount.
  //
  // Deliberately NOT gated on centreId. A main centre with no centres yet
  // resolves centreId to '' — gating on it meant no socket existed at all on
  // that screen, so the `centres:changed` broadcast announcing the very centre
  // being added could never arrive and the gate sat there until a reload.
  // The server joins the per-scope room only for valid ids (see joinScope), so
  // an empty centre simply gets the per-user and admin rooms, which is exactly
  // what global master-data events like centres/main-centres/sessions use.
  useEffect(() => {
    if (!shellReady || !sessionId) return;
    connectSocket(centreId, sessionId);
    return () => { disconnectSocket(); };
  }, [shellReady, centreId, sessionId]);

  // Step 4 — notification polling: runs after scope headers are set, polls every 30s
  useEffect(() => {
    if (!shellReady || !centreId) return;

    let alive = true;

    const tick = async () => {
      try {
        const { data } = await notificationService.getNotifications({ limit: 1 });
        if (alive) useAppShellStore.getState().setUnreadCount(data.unreadCount);
      } catch { /* empty */ }
    };

    tick();
    const t = setInterval(tick, 30_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [shellReady, centreId, sessionId]);

  // Deps mirror connectSocket's exactly: it disconnects and REPLACES the socket
  // whenever the centre or session changes, so a listener that doesn't re-run on
  // both stays bound to the destroyed instance and silently stops firing.
  // Step 5 — Main centres and centres are global master data (not centre/session
  // scoped): a `main-centres:changed` / `centres:changed` socket event (admins-room
  // broadcast, includes the actor) means re-fetch both lists so the topbar
  // switcher stays in sync across every admin the moment one is added, renamed or
  // deactivated. Only admins receive these events server-side, but guard on role
  // too to skip the listeners entirely for staff.
  useEffect(() => {
    if (!shellReady || user?.role !== 'admin') return;
    const socket = getSocket();
    if (!socket) return;

    const refreshCentres = () => {
      Promise.all([authService.getMainCentres(), authService.getCentres()])
        .then(([mainRes, centreRes]) => {
          const centres: Centre[] = centreRes.data.items.filter((c) => c.mainCentreId === mainCentreId);
          useAppShellStore.getState().setMainCentres(mainRes.data.items);
          useAppShellStore.getState().setCentres(centres);
          // A scope we were USING may have just been deactivated/removed — reload
          // so the app re-bootstraps onto a sane one, the same mechanism
          // ScopeSwitcher uses for any change.
          //
          // The `!!id` guards are load-bearing: a main centre with no centres
          // resolves centreId to '', and without them "no centre selected" reads
          // as "my centre vanished" — which reloads, reconnects, fires this
          // handler again on `connect`, and loops forever. The repeated reloads
          // then race /auth/refresh, whose token rotates on use, so a reused one
          // 401s and logs the user out.
          const mainGone = !!mainCentreId && !mainRes.data.items.some((m) => m._id === mainCentreId);
          const centreGone =
            !!centreId && centreId !== ALL_CENTRES && !centres.some((c) => c._id === centreId);
          if (mainGone || centreGone) {
            window.location.reload();
            return;
          }

          // We had no centre (an empty main centre) and one now exists — adopt it
          // in place rather than reloading. This is what clears the "no centres
          // yet" gate the moment a centre is added from it.
          if (!centreId && centres.length) {
            const next = centres.find((c) => c.active) ?? centres[0];
            if (next) useAppShellStore.getState().setCentreId(next._id);
          }
        })
        .catch(() => {});
    };

    socket.on('main-centres:changed', refreshCentres);
    socket.on('centres:changed', refreshCentres);
    socket.on('connect', refreshCentres);

    return () => {
      socket.off('main-centres:changed', refreshCentres);
      socket.off('centres:changed', refreshCentres);
      socket.off('connect', refreshCentres);
    };
  }, [shellReady, centreId, sessionId, mainCentreId, user?.role]);

  // Step 5b — Academic sessions are global master data that BOTH roles switch
  // between, so unlike `centres:changed` this listener is not admin-only. A
  // session can appear at any time: filing an admission under a year never used
  // before creates one on demand, as does the financial year rolling over. The
  // server broadcasts to every client; without this the new year would only show
  // up after a page reload.
  useEffect(() => {
    if (!shellReady) return;
    const socket = getSocket();
    if (!socket) return;

    socket.on('sessions:changed', refreshSessions);
    socket.on('connect', refreshSessions);

    return () => {
      socket.off('sessions:changed', refreshSessions);
      socket.off('connect', refreshSessions);
    };
  }, [shellReady, centreId, sessionId]);

  // Step 6 — Banks/Courses are per-centre master data. Their dropdown lists
  // (Add Admission/Installment) are cached at module level, independent of
  // React Query, so a `banks:changed` / `courses:changed` event needs its own
  // listener here (not just inside Settings) to keep an already-open form's
  // dropdown fresh without waiting for a reload. Skip when the event names a
  // different centre — that centre's banks/courses can't affect this dropdown.
  useEffect(() => {
    if (!shellReady) return;
    const socket = getSocket();
    if (!socket) return;

    const onBanksChanged = (payload?: { centreId?: string }) => {
      if (payload?.centreId != null && payload.centreId !== centreId) return;
      invalidateDropdown('banks');
    };
    const onCoursesChanged = (payload?: { centreId?: string }) => {
      if (payload?.centreId != null && payload.centreId !== centreId) return;
      invalidateDropdown('courses');
    };

    socket.on('banks:changed', onBanksChanged);
    socket.on('courses:changed', onCoursesChanged);

    return () => {
      socket.off('banks:changed', onBanksChanged);
      socket.off('courses:changed', onCoursesChanged);
    };
  }, [shellReady, centreId, sessionId]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active, textarea:-webkit-autofill, textarea:-webkit-autofill:hover, textarea:-webkit-autofill:focus, select:-webkit-autofill, select:-webkit-autofill:hover, select:-webkit-autofill:focus':
            {
              WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
              WebkitTextFillColor: `${theme.palette.grey[600]} !important`,
              caretColor: theme.palette.grey[600],
              transition: 'background-color 9999s ease-in-out 0s',
            },
        }}
      />
      <AppRouter />
    </ThemeProvider>
  );
};

const App = () => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <QueryClientProvider client={queryClient}>
      <ThemedApp />
      {env.isDev && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </LocalizationProvider>
);

export default App;
